import React from 'react';
import { useState } from 'react';
import { Calendar, Truck, Shield, Star, Clock, Award, X, Phone, Mail } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const LocationsPage = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Location <span className="heading-accent">Premium</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              Location de pianos pour particuliers (anniversaires, réceptions...) et professionnels (concerts, événements...). 
              Livraison du matériel incluse avec service haut de gamme et accompagnement personnalisé.
            </p>
          </div>
        </div>
      </section>

      {/* Section Location Mensuelle mise en avant */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <div className="glass-card rounded-3xl p-12 max-w-5xl mx-auto text-center relative overflow-hidden">
              {/* Effet de lumière */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center space-x-3 mb-6">
                  <Calendar className="h-12 w-12 text-yellow-400" />
                  <h2 className="text-4xl font-light text-white">
                    Location <span className="heading-accent font-semibold">Mensuelle</span>
                  </h2>
                </div>
                
                <p className="text-xl text-white/80 font-light mb-8 max-w-3xl mx-auto leading-relaxed">
                  Profitez d'un piano de qualité chez vous avec notre service de location mensuelle flexible. 
                  Idéal pour l'apprentissage, la pratique ou simplement le plaisir de jouer.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white/5 rounded-2xl p-6 border border-yellow-400/20">
                    <Star className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-2">Flexibilité Totale</h4>
                    <p className="text-white/60 font-light text-sm">Durée adaptable selon vos besoins</p>
                  </div>
                  
                  <div className="bg-white/5 rounded-2xl p-6 border border-yellow-400/20">
                    <Truck className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-2">Livraison Incluse</h4>
                    <p className="text-white/60 font-light text-sm">Installation et accord professionnel</p>
                  </div>
                  
                  <div className="bg-white/5 rounded-2xl p-6 border border-yellow-400/20">
                    <Shield className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-2">Entretien Garanti</h4>
                    <p className="text-white/60 font-light text-sm">Maintenance et accord réguliers</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-400/20 to-yellow-400/10 rounded-2xl p-6 mb-8 border border-yellow-400/30">
                  <p className="text-white font-light text-lg mb-4">
                    <span className="text-yellow-400 font-semibold">Tarifs personnalisés</span> selon le modèle et la durée
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => setShowAppointmentModal(true)}
                    className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide inline-block text-center"
                  >
                    Demander un Devis
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de location */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h3 className="text-3xl font-light text-center text-white mb-12">
              Nos <span className="heading-accent">Formules</span>
            </h3>
            
            <div className="grid lg:grid-cols-2 gap-12 mb-20">
              <div className="glass-card interactive-card rounded-3xl overflow-hidden light-effect">
                <div className="relative h-64">
                  <img
                    src="https://images.pexels.com/photos/1246437/pexels-photo-1246437.jpeg"
                    alt="Piano de concert"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute top-6 left-6">
                    <span className="bg-yellow-400/20 backdrop-blur-sm text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold border border-yellow-400/30">
                      Particuliers
                    </span>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-semibold text-white mb-4">Location Particuliers</h3>
                  <p className="text-white/70 font-light leading-relaxed mb-6">
                    Pianos pour anniversaires, réceptions, mariages et événements privés. 
                    Livraison du matériel, installation et accord professionnel inclus.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Award className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Pianos de qualité</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Truck className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Livraison du matériel</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Accord avant événement</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-3xl overflow-hidden light-effect">
                <div className="relative h-64">
                  <img
                    src="https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg"
                    alt="Piano domestique"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute top-6 left-6">
                    <span className="bg-yellow-400/20 backdrop-blur-sm text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold border border-yellow-400/30">
                      Professionnels
                    </span>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-semibold text-white mb-4">Location Professionnels</h3>
                  <p className="text-white/70 font-light leading-relaxed mb-6">
                    Pianos pour concerts, événements professionnels, écoles et conservatoires. 
                    Livraison du matériel avec possibilité d'achat.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Événements professionnels</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Truck className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Livraison du matériel</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Star className="h-5 w-5 text-yellow-400" />
                      <span className="text-white/80 font-light">Option d'achat</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services inclus */}
            <h3 className="text-3xl font-light text-center text-white mb-12">
              Services <span className="heading-accent">Inclus</span>
            </h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  icon: Calendar,
                  title: "Flexibilité Totale",
                  description: "De 1 jour à plusieurs années selon vos besoins"
                },
                {
                  icon: Truck,
                  title: "Transport Premium",
                  description: "Livraison du matériel et installation par nos experts"
                },
                {
                  icon: Shield,
                  title: "Assurance Complète",
                  description: "Instruments assurés et régulièrement entretenus"
                },
                {
                  icon: Clock,
                  title: "Maintenance 24/7",
                  description: "Intervention rapide et accord professionnel"
                }
              ].map((service, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                  <service.icon className="h-10 w-10 text-yellow-400 mx-auto mb-6" />
                  <h4 className="text-lg font-semibold text-white mb-3">{service.title}</h4>
                  <p className="text-white/60 font-light text-sm leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-6">
                Prêt pour votre <span className="heading-accent">Événement Exceptionnel</span> ?
              </h3>
              <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
                Contactez-nous pour un devis personnalisé et découvrez nos solutions 
                de location adaptées à vos besoins spécifiques.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowAppointmentModal(true)}
                  className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide inline-block text-center"
                >
                  Appelez-nous
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de prise de rendez-vous */}
      {showAppointmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">Prendre Rendez-vous</h3>
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="glass-card rounded-2xl p-6">
                  <Phone className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Appelez-nous</h4>
                  <div className="space-y-2 text-white/70">
                    <p>Magasin : <a href="tel:+33389542003" className="text-yellow-400 hover:text-yellow-300 transition-colors">03 89 54 20 03</a></p>
                    <p>Mobile : <a href="tel:+33607971604" className="text-yellow-400 hover:text-yellow-300 transition-colors">06 07 97 16 04</a></p>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-6">
                  <Mail className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Écrivez-nous</h4>
                  <p className="text-white/70">
                    <a href="mailto:musique.buchta@gmail.com" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                      musique.buchta@gmail.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-8 py-3 rounded-full font-light transition-all duration-300 w-full"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsPage;